import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import noop from 'lodash/noop';

import { ModelFile } from '../../components/model-context/ModelContext.tsx';
import { Folder } from '../../types/folder.ts';
import { NewProject, Project } from '../../types/project.ts';

export interface ProjectsState {
  projects: Project[];
  exampleProjects: Project[];
  folder?: Folder;
  folderTree?: Folder;
  newProject: NewProject;
  models: ModelFile[];
  // pagination
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  hasNext: boolean;
  currentFolderId?: number;
}

const initialState: ProjectsState = {
  projects: [],
  exampleProjects: [],
  folder: undefined,
  folderTree: undefined,
  newProject: {
    name: '',
    description: '',
    tolerance: 0,
    maintenance: 0,
    latitude: 0,
    longitude: 0,
    previewImageBase64: '',
    props: [],
    lights: [],
    layers: [],
  },
  models: [],
  page: 0,
  pageSize: 20,
  totalElements: 0,
  totalPages: 0,
  hasNext: false,
  currentFolderId: undefined,
};

interface UpdateProjectPayload {
  projectId: number;
  newName: string;
}

interface UpdateFolderPayload {
  folderId: number;
  newName: string;
}

interface MoveProjectPayload {
  projectId: number;
  targetFolderId: number | null;
}

interface CreateFolderPayload {
  folderName: string;
  parentId: number | null;
}

interface UpdateProjectTemplatePayload {
  projectId: number;
  template: boolean;
}

export const projectsReducer = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    loadProjects: (_state: ProjectsState, _action: PayloadAction<number | undefined>) => noop(),
    loadExampleProjects: (_state: ProjectsState) => noop(),
    cloneExampleProject: (_state: ProjectsState, _action: PayloadAction<number>) => noop(),
    resetProjects: (state: ProjectsState) => {
      state.projects = [];
      state.page = 0;
      state.totalElements = 0;
      state.totalPages = 0;
      state.hasNext = false;
    },
    setExampleProjects: (state: ProjectsState, action: PayloadAction<Project[]>) => {
      state.exampleProjects = action.payload;
    },
    setCurrentFolderId: (state: ProjectsState, action: PayloadAction<number | undefined>) => {
      state.currentFolderId = action.payload;
    },
    setProjectsPage: (
      state: ProjectsState,
      action: PayloadAction<{
        content: Project[];
        page: number;
        pageSize: number;
        totalElements: number;
        totalPages: number;
        hasNext: boolean;
      }>,
    ) => {
      state.projects = action.payload.content;
      state.page = action.payload.page;
      state.pageSize = action.payload.pageSize;
      state.totalElements = action.payload.totalElements;
      state.totalPages = action.payload.totalPages;
      state.hasNext = action.payload.hasNext;
    },
    appendProjectsPage: (
      state: ProjectsState,
      action: PayloadAction<{
        content: Project[];
        page: number;
        pageSize: number;
        totalElements: number;
        totalPages: number;
        hasNext: boolean;
      }>,
    ) => {
      state.projects = [...state.projects, ...action.payload.content];
      state.page = action.payload.page;
      state.pageSize = action.payload.pageSize;
      state.totalElements = action.payload.totalElements;
      state.totalPages = action.payload.totalPages;
      state.hasNext = action.payload.hasNext;
    },
    loadMoreProjects: (_state: ProjectsState) => noop(),
    createFolder: (_state: ProjectsState, _action: PayloadAction<CreateFolderPayload>) => {},
    updateFolder: (_state: ProjectsState, _action: PayloadAction<UpdateFolderPayload>) => noop(),
    updateProject: (_state: ProjectsState, _action: PayloadAction<UpdateProjectPayload>) => noop(),
    renameProject: (state: ProjectsState, action: PayloadAction<UpdateProjectPayload>) => {
      const { projectId, newName } = action.payload;

      state.projects = state.projects.map((project) => {
        if (project.id === projectId) {
          return {
            ...project,
            name: newName,
          };
        }

        return project;
      });
    },
    moveProject: (_state: ProjectsState, _action: PayloadAction<MoveProjectPayload>) => noop(),
    loadFolder: (_state: ProjectsState, _action: PayloadAction<number | undefined>) => noop(),
    setFolder: (state: ProjectsState, action: PayloadAction<Folder>) => {
      state.folder = action.payload;
    },
    loadFolderTree: (_state: ProjectsState, _action: PayloadAction<number | undefined>) => noop(),
    setFolderTree: (state: ProjectsState, action: PayloadAction<Folder>) => {
      state.folderTree = action.payload;
    },
    deleteFolder: (_state: ProjectsState, _action: PayloadAction<number>) => noop(),
    setNewProject: (state: ProjectsState, action: PayloadAction<NewProject>) => {
      state.newProject = action.payload;
    },
    setModels: (state: ProjectsState, action: PayloadAction<ModelFile[]>) => {
      state.models = action.payload;
    },
    createProject: (_state: ProjectsState, _action: PayloadAction<FormData>) => noop(),
    startTamanOptimization: (_state: ProjectsState) => {},
    finishTamanOptimization: (_state: ProjectsState) => {},
    startAutoPositioning: (_state: ProjectsState) => {},
    finishAutoPositioning: (_state: ProjectsState) => {},
    resetNewProject: (state: ProjectsState) => {
      state.newProject = initialState.newProject;
    },
    deleteProject: (_state: ProjectsState, _action: PayloadAction<number>) => noop(),
    removeProject: (state: ProjectsState, action: PayloadAction<number>) => {
      state.projects = state.projects.filter((project) => project.id !== action.payload);
    },
    setProjectById: (state: ProjectsState, action: PayloadAction<{ project: Project; template: boolean }>) => {
      const { project: updatedProject, template } = action.payload;

      state.projects = state.projects.filter((project) => project.id !== updatedProject.id);
      state.exampleProjects = state.exampleProjects.filter((project) => project.id !== updatedProject.id);

      if (template) {
        state.exampleProjects = [updatedProject, ...state.exampleProjects];
      }
      state.projects = [updatedProject, ...state.projects];
    },
    updateProjectName: (_state: ProjectsState, _action: PayloadAction<{ projectId: number; name: string }>) => noop(),
    updateProjectTemplate: (_state: ProjectsState, _action: PayloadAction<UpdateProjectTemplatePayload>) => noop(),
  },
});
